import { useCallback, useEffect, useState } from 'react';
import type { MaskedSettings, PublicSettings } from '@ozon-ai/apigateway/contracts';
import type { TaxRegime } from '@ozon-ai/analysis/contracts';
import { Button } from '@/components/ui/button.tsx';
import { Label } from '@/components/ui/label.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { api } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useTelegram } from '../../../context/TelegramContext';
import { SETTINGS_ANALYSIS_HUB } from '../settings-paths';
import {
  Field,
  NumberField,
  SettingsErrorAlert,
  SettingsForm,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
  SettingsSuccessAlert,
  splitList,
} from '../settings-shared';

const TAX_REGIME_OPTIONS: { value: TaxRegime; label: string }[] = [
  { value: 'none', label: 'Не учитывать' },
  { value: 'percent_of_revenue', label: '% от выручки' },
  { value: 'usn_income', label: 'УСН доходы' },
  { value: 'usn_income_expense', label: 'УСН доходы-расходы' },
];

export function SettingsAnalysisPage() {
  const { webApp } = useTelegram();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [botToken, setBotToken] = useState('');
  const [adminChatIds, setAdminChatIds] = useState('');
  const [allowedUserIds, setAllowedUserIds] = useState('');
  const [intervalHours, setIntervalHours] = useState(72);
  const [priceDrop, setPriceDrop] = useState(5);
  const [minMargin, setMinMargin] = useState(15);
  const [maxRetries, setMaxRetries] = useState(3);
  const [taxRegime, setTaxRegime] = useState<TaxRegime>('none');
  const [taxRate, setTaxRate] = useState(0);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await api.get<MaskedSettings>('/settings');
      if (data) {
        setBotToken(data.secrets.telegram.botToken);
        setAdminChatIds((data.secrets.telegram.adminChatIds || []).join(', '));
        setAllowedUserIds((data.public.allowedUserIds || []).join(', '));
        setIntervalHours(data.public.analysis.intervalHours);
        setPriceDrop(data.public.analysis.priceDropThresholdPercent);
        setMinMargin(data.public.analysis.minMarginPercent);
        setMaxRetries(data.public.analysis.maxRetries);
        setTaxRegime(data.public.analysis.tax?.regime ?? 'none');
        setTaxRate(data.public.analysis.tax?.ratePercent ?? 0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveAnalysis() {
    setBusy(true);
    setMessage(null);
    try {
      await api.put('/settings/secrets', {
        telegram: {
          botToken,
          adminChatIds: splitList(adminChatIds),
        },
      });
      const publicSettings: Partial<PublicSettings> = {
        analysis: {
          intervalHours: Number(intervalHours),
          priceDropThresholdPercent: Number(priceDrop),
          minMarginPercent: Number(minMargin),
          maxRetries: Number(maxRetries),
          tax: {
            regime: taxRegime,
            ratePercent: Number(taxRate),
          },
        },
        allowedUserIds: splitList(allowedUserIds)
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id)),
      };
      await api.put('/settings/public', publicSettings);
      setMessage('Параметры сохранены');
      webApp?.HapticFeedback?.notificationOccurred('success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <SettingsSubPageShell title="Пороги и доступ" backTo={SETTINGS_ANALYSIS_HUB}>
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}
      {message && <SettingsSuccessAlert>{message}</SettingsSuccessAlert>}

      <SettingsFormCard>
        <SettingsHint>
          Доступ к Mini App и уведомления. Секреты показываются как last4.
        </SettingsHint>
        <SettingsForm onSubmit={saveAnalysis} className="mt-4">
          <Field label="Allowlist user IDs Telegram" id="allowedUserIds" value={allowedUserIds} onChange={setAllowedUserIds} />
          <Field label="Telegram bot token" id="botToken" value={botToken} onChange={setBotToken} />
          <Field
            label="Admin chat IDs (через запятую)"
            id="adminChatIds"
            value={adminChatIds}
            onChange={setAdminChatIds}
          />
          <NumberField label="Интервал анализа, часы" id="intervalHours" value={intervalHours} onChange={setIntervalHours} />
          <NumberField label="Порог падения витрины, %" id="priceDrop" value={priceDrop} onChange={setPriceDrop} />
          <NumberField label="Мин. маржа, %" id="minMargin" value={minMargin} onChange={setMinMargin} />
          <NumberField label="Max retry действий" id="maxRetries" value={maxRetries} onChange={setMaxRetries} />
          <div className="flex flex-col gap-2">
            <Label>Налог (для Product Analysis)</Label>
            <Select
              value={taxRegime}
              items={TAX_REGIME_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
              onValueChange={(value) => value && setTaxRegime(value as TaxRegime)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Режим налога" />
              </SelectTrigger>
              <SelectContent>
                {TAX_REGIME_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <NumberField label="Ставка налога, %" id="taxRate" value={taxRate} onChange={setTaxRate} />
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            Сохранить
          </Button>
        </SettingsForm>
      </SettingsFormCard>
    </SettingsSubPageShell>
  );
}
